import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSearchParam } from "react-use";
import { LiveHodoContext } from "@contexts/LiveHodoContext";
import SidePane from "@layouts/SidePane";
import { ErrorDialog } from "@primitives/DialogContent";
import i18n from "i18next";
import { v4 as uuid } from "uuid";
// import getToken from "../services/tokenService";
import { useHMSActions } from "@100mslive/react-sdk";
import { Box, Flex, Loading } from "@100mslive/roomkit-react";
import PreviewContainer from "./Preview/PreviewContainer";
import { Header } from "./Header";
import { useSetUiSettings, useTokenEndpoint } from "./AppData/useUISettings";
import { useNavigation } from "@components/hooks/useNavigation";
import { useGetUserData } from "@hooks/useGetUserData";
import { isJSON, isStreamingKit } from "../common/utils";
import { ROLES } from "@common/roles";
import { isPMDT } from "@common/utils";
import request from "@services/request";
import {
  QUERY_PARAM_AUTH_TOKEN,
  QUERY_PARAM_NAME,
  QUERY_PARAM_PREVIEW_AS_ROLE,
  QUERY_PARAM_SKIP_PREVIEW,
  QUERY_PARAM_SKIP_PREVIEW_HEADFUL,
  UI_SETTINGS,
} from "../common/constants";
const PasswordGate = React.lazy(() => import("./Preview/PasswordGate"));

/**
 * query params exposed -
 * skip_preview=true => used by recording and streaming service, skips preview and directly joins
 *                      header and footer don't show up in this case
 * skip_preview_headful=true => used by automation testing to skip preview without impacting the UI
 * name=abc => gives the initial name for the peer joining
 * auth_token=123 => uses the passed in token to join instead of fetching from token endpoint
 * ui_mode=activespeaker => lands in active speaker mode after joining the room
 */

const env = process.env.REACT_APP_ENV;
const PreviewScreen = React.memo(({ authTokenByRoomCodeEndpoint }) => {
  const {
    stateCtx: {
      isJoinCabin,
      isMdt,
      mdtRoomDetail,
      isMeeting,
      isTelemedicine,
      isLivestream,
      isConference,
      channelRoom,
    },
    setSelectedCabinData,
    setCircleRoomDetail,
    setMdtRoomDetail,
    setIsPMDT,
    getConferenceSchedules,
    getToken,
  } = useContext(LiveHodoContext);
  const { t } = useTranslation();

  const navigate = useNavigation();
  const hmsActions = useHMSActions();
  const tokenEndpoint = useTokenEndpoint();
  const [, setIsHeadless] = useSetUiSettings(UI_SETTINGS.isHeadless);
  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url
  const userData = useGetUserData();
  const [token, setToken] = useState(null);
  const [passwordAccepted, setPasswordAccepted] = useState(false);
  const [error, setError] = useState({ title: "", body: "" });
  // way to skip preview for automated tests, beam recording and streaming
  const beamInToken = useSearchParam("token") === "beam_recording"; // old format to remove
  let skipPreview = useSearchParam(QUERY_PARAM_SKIP_PREVIEW) === "true";
  // use this field to join directly for quick testing while in local
  const directJoinHeadfulFromEnv =
    process.env.REACT_APP_HEADLESS_JOIN === "true";
  const directJoinHeadful =
    useSearchParam(QUERY_PARAM_SKIP_PREVIEW_HEADFUL) === "true" ||
    directJoinHeadfulFromEnv;
  skipPreview = skipPreview || beamInToken || directJoinHeadful;
  const [initialName, setInitialName] = useState("");
  const nameRaw =
    useSearchParam(QUERY_PARAM_NAME) || (skipPreview ? "Beam" : "");
  const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE);
  let authToken = useSearchParam(QUERY_PARAM_AUTH_TOKEN);

  // const [isNoiseSuppressedState, , handleNoiseSuppression] =
  //   useNoiseSuppression();

  // useEffect(() => {
  //   if (!isNoiseSuppressedState) return;
  //   handleNoiseSuppression();
  // }, [handleNoiseSuppression, isNoiseSuppressedState]);

  const checkMyRole = useCallback(async () => {
    if (!userData?.id) return;

    try {
      if (isMeeting || isMdt || isTelemedicine || isPMDT()) {
        return true;
      }

      const circle_id = Number(localStorage.getItem("circle_id"));
      const user_id = userData?.id;
      const res = await request.checkMyRoleConferenceRoom({
        circle_id,
        user_id,
      });

      if (!res?.data) return false;

      const myRoleConferenceRoom = res?.data;

      const keyRole = isStreamingKit() ? "livestream" : "conference";
      const roles =
        typeof myRoleConferenceRoom?.one_hundred_ms_role === "string" &&
        isJSON(myRoleConferenceRoom?.one_hundred_ms_role)
          ? JSON.parse(myRoleConferenceRoom?.one_hundred_ms_role)
          : {
              [keyRole]: myRoleConferenceRoom?.one_hundred_ms_role,
            };
      const myRole = isStreamingKit()
        ? roles[keyRole] || ROLES.HLS_VIEWER
        : roles[keyRole] || ROLES.GUEST;

      if (myRole === userRole) {
        return true;
      }

      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [userData, isMeeting, isMdt, isTelemedicine, isPMDT]);

  useEffect(() => {
    setSelectedCabinData(null);
  }, []);

  const getCircleDetail = useCallback(async () => {
    const circleId = window.localStorage.getItem("circle_id");
    if (!circleId) return;

    try {
      const res = await request.getCircleDetailById(circleId);
      if (!res?.data) return;
      setCircleRoomDetail(res?.data);
    } catch (error) {
      console.log(error);
    }
  });

  const getRoomStreamDetail = useCallback(async () => {
    try {
      if (!urlRoomId) return;

      const res = await request.getRoomStreamByRoomId(urlRoomId);
      if (!res?.data) return;
      setCircleRoomDetail(res?.data);
    } catch (error) {
      console.log(error);
    }
  }, [urlRoomId]);

  const getRoomConferenceDetail = useCallback(async () => {
    try {
      if (!urlRoomId) return;

      const res = await request.getRoomConferenceByRoomId(urlRoomId);
      if (!res?.data) return;
      setCircleRoomDetail(res?.data);

      const params = {
        circle_id: Number(localStorage.getItem("circle_id")),
        sort_by: "schedule_date",
        order: "asc",
        page_size: 1000,
        page_num: 1,
      };
      await getConferenceSchedules(params);
    } catch (error) {
      console.log(error);
    }
  }, [urlRoomId]);

  const getRoomMdtDetail = useCallback(async () => {
    try {
      if (!urlRoomId) return;
      let detection = null;
      try {
        detection = await request.detectRoomType(urlRoomId);
        if (detection?.type) setIsPMDT(detection.type === "pmdt");
      } catch (e) {
        // detection failed; fall back to normal fetch below
      }

      if (detection && detection.res) {
        const roomDetail = detection.res.data?.data || detection.res.data;
        if (!roomDetail) return;
        setMdtRoomDetail(roomDetail);
        return;
      }

      const usePMDT = detection ? detection.type === "pmdt" : isPMDT();
      const res = await request.getRoomMdtByRoomId(urlRoomId, {
        is_public_mdt: +usePMDT,
      });
      if (!res?.data) return;
      const roomDetail = res.data?.data || res.data;
      setMdtRoomDetail(roomDetail);
    } catch (error) {
      console.log(error);
    }
  }, [urlRoomId, setIsPMDT]);

  useEffect(() => {
    (async () => {
      if (authToken) {
        setToken(authToken);
        return;
      }

      if (!tokenEndpoint || !urlRoomId) {
        return;
      }

      const roomCode = !userRole && urlRoomId;

      if (userRole) {
        if (!window.localStorage.getItem("token") && !userData?.id) {
          try {
            const current = window.location.pathname + window.location.search;
            window.location.replace(
              `/sign-in?redirect=${encodeURIComponent(current)}`
            );
          } catch (e) {
            window.location.replace(`/sign-in`);
          }
          return;
        }

        if (!userData?.id) return;

        const isJoined = await checkMyRole();

        if (!isJoined && !isStreamingKit()) {
          setError(
            convertPreviewError({
              action: "CHECK_ROLE",
              title: t("lbl_error_check_role"),
              body: t("msg_error_check_role"),
            })
          );
          return;
        }

        const getTokenFn = roomCode
          ? () =>
              hmsActions.getAuthTokenByRoomCode(
                { roomCode },
                { endpoint: authTokenByRoomCodeEndpoint }
              )
          : () => getToken(userData?.id || uuid(), userRole, urlRoomId);

        getTokenFn()
          .then(token => {
            setToken(token);
          })
          .catch(error => {
            setError(convertPreviewError(error));
          });

        isStreamingKit()
          ? getRoomStreamDetail()
          : isMdt || isPMDT()
            ? getRoomMdtDetail()
            : isConference
              ? getRoomConferenceDetail()
              : isMeeting
                ? getCircleDetail()
                : isTelemedicine
                  ? null // Call data patient visit
                  : null;
      } else {
        const getTokenFn = roomCode
          ? () =>
              hmsActions.getAuthTokenByRoomCode(
                { roomCode },
                { endpoint: authTokenByRoomCodeEndpoint }
              )
          : () =>
              getToken(
                `https://prod-in2.100ms.live/hmsapi/man-videoconf-1111.app.100ms.live/api/token`,
                uuid(),
                userRole,
                urlRoomId
              );

        getTokenFn()
          .then(token => {
            setToken(token);
          })
          .catch(error => {
            setError(convertPreviewError(error));
          });
      }

      const name =
        userRole === ROLES.E_V || userRole === ROLES.F_V
          ? userRole.replace("-", "").toUpperCase()
          : `${userData?.name}` || nameRaw;
      setInitialName(name);
    })();
  }, [
    hmsActions,
    tokenEndpoint,
    urlRoomId,
    userRole,
    userData,
    authToken,
    authTokenByRoomCodeEndpoint,
  ]);

  useEffect(() => {
    if (!userData?.id || !channelRoom) return;
    const handler = setTimeout(() => {
      channelRoom?.whisper("userOnline", {
        id: userData.id,
        name: userData?.name,
        role: "doctor",
      });
    }, 1000);
    return () => clearTimeout(handler);
  }, [userData, channelRoom]);

  const onJoin = () => {
    !directJoinHeadful && setIsHeadless(skipPreview);

    let meetingURL = "";

    if (isPMDT()) {
      meetingURL = `/pmdt/${urlRoomId}`;
    } else if (isMdt) {
      meetingURL = `/mdt/${urlRoomId}`;
    } else if (isMeeting) {
      meetingURL = `/meeting/${urlRoomId}`;
    } else if (isTelemedicine) {
      meetingURL = `/telemedicine/${urlRoomId}`;
    } else if (isLivestream) {
      meetingURL = `/streaming/${urlRoomId}`;
    } else if (isConference && isJoinCabin && userRole === ROLES.GUEST) {
      meetingURL = `/select/${urlRoomId}`;
    } else if (isConference && isJoinCabin && userRole !== ROLES.GUEST) {
      meetingURL = `/conference/${urlRoomId}`;
    } else {
      meetingURL = `/${urlRoomId}`;
    }

    if (userRole) {
      meetingURL += `/${userRole}`;
    }
    console.log(meetingURL);
    navigate(meetingURL);
  };

  if (error.title) {
    return <ErrorDialog title={error.title}>{error.body}</ErrorDialog>;
  }

  return (
    <Flex
      direction="column"
      css={{
        size: "100%",
        position: "relative",
      }}
    >
      <Box
        css={{ h: "$18", "@md": { h: "$17", flexShrink: 0 } }}
        data-testid="header"
      >
        <Header isPreview={true} />
      </Box>
      <Flex
        css={{ flex: "1 1 0", position: "relative", overflowY: "auto" }}
        justify="center"
        align="center"
      >
        {token &&
        (!(isMdt || isPMDT()) ||
          !mdtRoomDetail ||
          mdtRoomDetail?.locked !== 1 ||
          passwordAccepted) ? (
          <>
            <PreviewContainer
              initialName={initialName}
              skipPreview={skipPreview}
              env={env}
              onJoin={onJoin}
              token={token}
              asRole={previewAsRole}
            />
          </>
        ) : (isMdt || isPMDT()) &&
          mdtRoomDetail &&
          mdtRoomDetail?.locked === 1 ? (
          <Box css={{ p: "$8", maxWidth: 560, width: "100%" }}>
            <React.Suspense fallback={<Loading size={60} />}>
              <PasswordGate
                roomDetail={mdtRoomDetail}
                onSuccess={() => setPasswordAccepted(true)}
              />
            </React.Suspense>
          </Box>
        ) : (
          <Loading size={100} />
        )}
        <SidePane
          css={{
            position: "unset",
            mr: "$10",
            "@lg": { position: "fixed", mr: "$0" },
          }}
        />
      </Flex>
    </Flex>
  );
});

const convertPreviewError = error => {
  console.error("[error]", { error });
  if (error.action === "GET_TOKEN" && error.code === 403) {
    return {
      title: i18n.t("lbl_room_code_is_disabled"),
      body: ErrorWithSupportLink(i18n.t("msg_room_code_is_disabled")),
    };
  } else if (error.action === "GET_TOKEN" && error.code === 404) {
    return {
      title: i18n.t("lbl_room_code_does_not_exist"),
      body: ErrorWithSupportLink(i18n.t("msg_room_code_does_not_exist")),
    };
  } else if (error.action === "GET_TOKEN" && error.code === 2003) {
    return {
      title: i18n.t("lbl_endpoint_is_not_reachable"),
      body: ErrorWithSupportLink(
        i18n.t("fm_endpoint_is_not_reachable", { desc: error.description })
      ),
    };
  } else if (error.response && error.response.status === 404) {
    return {
      title: i18n.t("lbl_room_does_not_exist"),
      body: ErrorWithSupportLink(i18n.t("msg_room_does_not_exist")),
    };
  } else if (error.response && error.response.status === 403) {
    return {
      title: i18n.t("lbl_accessing_disabled"),
      body: ErrorWithSupportLink(i18n.t("msg_accessing_disabled")),
    };
  } else if (error.action === "USER_NOT_SIGN_IN") {
    return {
      title: error.title,
      body: ErrorWithSupportLink(error.body),
    };
  } else if (error.action === "CHECK_ROLE") {
    return {
      title: error.title,
      body: ErrorWithSupportLink(error.body),
    };
  } else {
    console.error("Token API Error", error);
    return {
      title: i18n.t("lbl_error_fetching_token"),
      body: ErrorWithSupportLink(i18n.t("msg_error_fetching_token")),
    };
  }
};

// const Link = styled("a", {
//   color: "#2f80e1",
// });

export const ErrorWithSupportLink = errorMessage => (
  <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
);

export default PreviewScreen;
